// store/slices/appSettingsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  // Config thunks
  createAppConfigThunk,
  updateAppConfigThunk,
  fetchAllConfigsThunk,
  fetchConfigByIdThunk,
  deleteConfigThunk,
  activateConfigThunk,
  cloneConfigThunk,

  // Banner thunks
  createBannerThunk,
  updateBannerThunk,
  fetchAllBannersThunk,
  fetchBannerByIdThunk,
  deleteBannerThunk,
  updateBannerStatusThunk,
  reorderBannersThunk,
  fetchBannerAnalyticsThunk,

  // Collection thunks
  createCollectionThunk,
  updateCollectionThunk,
  fetchAllCollectionsThunk,
  fetchCollectionByIdThunk,
  deleteCollectionThunk,
  updateCollectionStatusThunk,
  fetchCollectionProductsThunk,

  // Contact Info thunks
  createContactInfoThunk,
  updateContactInfoThunk,
  fetchAllContactInfoThunk,
  fetchContactInfoByIdThunk,
  deleteContactInfoThunk,

  // Feature Flags thunks
  updateFeatureFlagsThunk,
  bulkUpdateFeaturesThunk,

  // Home Layout thunks
  updateHomeLayoutThunk,
  previewHomeLayoutThunk,

  // Import/Export thunks
  importBannersThunk,
  exportConfigThunk,

  // Experiment thunks
  createExperimentThunk,
  updateExperimentThunk,
  fetchExperimentsThunk,
  fetchExperimentResultsThunk,

  // Actions
  clearAppSettingsError,
  resetSelectedConfig,
  resetSelectedBanner,
  resetSelectedCollection,
  setAppSettingsFilter,
} from "../thunks/appSettingsThunks";

const initialState = {
  // Configs
  configs: [],
  selectedConfig: null,

  // Banners
  banners: [],
  selectedBanner: null,
  bannerAnalytics: null,

  // Collections
  collections: [],
  selectedCollection: null,
  collectionProducts: [],

  // Contact Info
  contactInfoList: [],
  selectedContactInfo: null,

  // Experiments
  experiments: [],
  selectedExperiment: null,
  experimentResults: null,

  // Preview
  homeLayoutPreview: null,

  // Filters
  filters: {
    configType: "",
    branchCode: "",
    isActive: null,
    search: "",
  },

  // Loading states
  loading: false,
  loadingList: false,
  loadingPreview: false,

  // Error handling
  error: null,
  successMessage: null,

  // Constants
  configTypes: [
    { value: "GLOBAL", label: "Global" },
    { value: "BRANCH", label: "Branch Specific" },
    { value: "REGION", label: "Region Specific" },
    { value: "CUSTOMER_GROUP", label: "Customer Group" },
  ],

  sectionTypes: [
    { value: "BANNER", label: "Banner" },
    { value: "CATEGORIES", label: "Categories" },
    { value: "COLLECTIONS", label: "Collections" },
    { value: "FEATURED_PRODUCTS", label: "Featured Products" },
    { value: "NEW_ARRIVALS", label: "New Arrivals" },
    { value: "OFFERS", label: "Offers" },
    { value: "QUICK_ACTIONS", label: "Quick Actions" },
    { value: "TRUST_INDICATORS", label: "Trust Indicators" },
    { value: "TESTIMONIALS", label: "Testimonials" },
    { value: "CUSTOM", label: "Custom" },
  ],

  actionTypes: [
    { value: "NAVIGATION", label: "App Navigation" },
    { value: "DEEPLINK", label: "Deep Link" },
    { value: "WEBVIEW", label: "Web View" },
    { value: "EXTERNAL", label: "External Link" },
    { value: "NONE", label: "No Action" },
  ],

  cardStyles: [
    { value: "STANDARD", label: "Standard" },
    { value: "GRADIENT", label: "Gradient" },
    { value: "IMAGE_OVERLAY", label: "Image Overlay" },
    { value: "MINIMAL", label: "Minimal" },
  ],
};

const appSettingsSlice = createSlice({
  name: "appSettings",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear error
      .addCase(clearAppSettingsError, (state) => {
        state.error = null;
      })
      // Reset selected items
      .addCase(resetSelectedConfig, (state) => {
        state.selectedConfig = null;
      })
      .addCase(resetSelectedBanner, (state) => {
        state.selectedBanner = null;
        state.bannerAnalytics = null;
      })
      .addCase(resetSelectedCollection, (state) => {
        state.selectedCollection = null;
        state.collectionProducts = [];
      })
      // Set filter
      .addCase(setAppSettingsFilter, (state, action) => {
        state.filters = { ...state.filters, ...action.payload };
      })

      // ===== APP CONFIG =====
      .addCase(createAppConfigThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createAppConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.configs.push(action.payload.data || action.payload);
        state.successMessage =
          action.payload.message || "Config created successfully";
      })
      .addCase(createAppConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAppConfigThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedConfig = action.payload.data || action.payload;
        state.configs = state.configs.map((config) =>
          config._id === updatedConfig._id ? updatedConfig : config
        );
        state.selectedConfig = updatedConfig;
        state.successMessage =
          action.payload.message || "Config updated successfully";
      })
      .addCase(updateAppConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllConfigsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAllConfigsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.configs = action.payload.data || action.payload;
      })
      .addCase(fetchAllConfigsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(fetchConfigByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConfigByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedConfig = action.payload.data || action.payload;
      })
      .addCase(fetchConfigByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteConfigThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.configs = state.configs.filter(
          (config) => config._id !== action.meta.arg
        );
        state.successMessage = "Config deleted successfully";
      })
      .addCase(deleteConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(activateConfigThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(activateConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        const activatedConfig = action.payload.data || action.payload;
        state.configs = state.configs.map((config) =>
          config._id === activatedConfig._id ? activatedConfig : config
        );
        state.successMessage = "Config activated successfully";
      })
      .addCase(activateConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(cloneConfigThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.configs.push(action.payload.data || action.payload);
        state.successMessage = "Config cloned successfully";
      })
      .addCase(cloneConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== BANNERS =====
      .addCase(createBannerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createBannerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.banners.push(action.payload.data || action.payload);
        state.successMessage =
          action.payload.message || "Banner created successfully";
      })
      .addCase(createBannerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBannerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBannerThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBanner = action.payload.data || action.payload;
        state.banners = state.banners.map((banner) =>
          banner._id === updatedBanner._id ? updatedBanner : banner
        );
        state.selectedBanner = updatedBanner;
        state.successMessage =
          action.payload.message || "Banner updated successfully";
      })
      .addCase(updateBannerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllBannersThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAllBannersThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.banners = action.payload.data || action.payload;
      })
      .addCase(fetchAllBannersThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(fetchBannerByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBannerByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBanner = action.payload.data || action.payload;
      })
      .addCase(fetchBannerByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteBannerThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBannerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = state.banners.filter(
          (banner) => banner._id !== action.meta.arg
        );
        state.successMessage = "Banner deleted successfully";
      })
      .addCase(deleteBannerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBannerStatusThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBannerStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBanner = action.payload.data || action.payload;
        state.banners = state.banners.map((banner) =>
          banner._id === updatedBanner._id ? updatedBanner : banner
        );
        state.successMessage = "Banner status updated successfully";
      })
      .addCase(updateBannerStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(reorderBannersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(reorderBannersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload.data || action.payload;
        state.successMessage = "Banners reordered successfully";
      })
      .addCase(reorderBannersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchBannerAnalyticsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBannerAnalyticsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bannerAnalytics = action.payload.data || action.payload;
      })
      .addCase(fetchBannerAnalyticsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== COLLECTIONS =====
      .addCase(createCollectionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createCollectionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.collections.push(action.payload.data || action.payload);
        state.successMessage =
          action.payload.message || "Collection created successfully";
      })
      .addCase(createCollectionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateCollectionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCollectionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCollection = action.payload.data || action.payload;
        state.collections = state.collections.map((collection) =>
          collection._id === updatedCollection._id
            ? updatedCollection
            : collection
        );
        state.selectedCollection = updatedCollection;
        state.successMessage =
          action.payload.message || "Collection updated successfully";
      })
      .addCase(updateCollectionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllCollectionsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAllCollectionsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.collections = action.payload.data || action.payload;
      })
      .addCase(fetchAllCollectionsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(fetchCollectionByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollectionByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCollection = action.payload.data || action.payload;
      })
      .addCase(fetchCollectionByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteCollectionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCollectionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.collections = state.collections.filter(
          (collection) => collection._id !== action.meta.arg
        );
        state.successMessage = "Collection deleted successfully";
      })
      .addCase(deleteCollectionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateCollectionStatusThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCollectionStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCollection = action.payload.data || action.payload;
        state.collections = state.collections.map((collection) =>
          collection._id === updatedCollection._id
            ? updatedCollection
            : collection
        );
        state.successMessage = "Collection status updated successfully";
      })
      .addCase(updateCollectionStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCollectionProductsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCollectionProductsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.collectionProducts = action.payload.data || action.payload;
      })
      .addCase(fetchCollectionProductsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== CONTACT INFO =====
      .addCase(createContactInfoThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createContactInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.contactInfoList.push(action.payload.data || action.payload);
        state.successMessage =
          action.payload.message || "Contact info created successfully";
      })
      .addCase(createContactInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateContactInfoThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContactInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedContact = action.payload.data || action.payload;
        state.contactInfoList = state.contactInfoList.map((contact) =>
          contact._id === updatedContact._id ? updatedContact : contact
        );
        state.selectedContactInfo = updatedContact;
        state.successMessage =
          action.payload.message || "Contact info updated successfully";
      })
      .addCase(updateContactInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllContactInfoThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAllContactInfoThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.contactInfoList = action.payload.data || action.payload;
      })
      .addCase(fetchAllContactInfoThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(fetchContactInfoByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContactInfoByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContactInfo = action.payload.data || action.payload;
      })
      .addCase(fetchContactInfoByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteContactInfoThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteContactInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.contactInfoList = state.contactInfoList.filter(
          (contact) => contact._id !== action.meta.arg
        );
        state.successMessage = "Contact info deleted successfully";
      })
      .addCase(deleteContactInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== FEATURE FLAGS =====
      .addCase(updateFeatureFlagsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFeatureFlagsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Feature flags updated successfully";
      })
      .addCase(updateFeatureFlagsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(bulkUpdateFeaturesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkUpdateFeaturesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Features updated successfully";
      })
      .addCase(bulkUpdateFeaturesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== HOME LAYOUT =====
      .addCase(updateHomeLayoutThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateHomeLayoutThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Home layout updated successfully";
      })
      .addCase(updateHomeLayoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(previewHomeLayoutThunk.pending, (state) => {
        state.loadingPreview = true;
      })
      .addCase(previewHomeLayoutThunk.fulfilled, (state, action) => {
        state.loadingPreview = false;
        state.homeLayoutPreview = action.payload.data || action.payload;
      })
      .addCase(previewHomeLayoutThunk.rejected, (state, action) => {
        state.loadingPreview = false;
        state.error = action.payload;
      })

      // ===== IMPORT/EXPORT =====
      .addCase(importBannersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(importBannersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Banners imported successfully";
      })
      .addCase(importBannersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(exportConfigThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Config exported successfully";
      })
      .addCase(exportConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== EXPERIMENTS =====
      .addCase(createExperimentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createExperimentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.experiments.push(action.payload.data || action.payload);
        state.successMessage =
          action.payload.message || "Experiment created successfully";
      })
      .addCase(createExperimentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateExperimentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateExperimentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedExperiment = action.payload.data || action.payload;
        state.experiments = state.experiments.map((exp) =>
          exp._id === updatedExperiment._id ? updatedExperiment : exp
        );
        state.successMessage = "Experiment updated successfully";
      })
      .addCase(updateExperimentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchExperimentsThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(fetchExperimentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.experiments = action.payload.data || action.payload;
      })
      .addCase(fetchExperimentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(fetchExperimentResultsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExperimentResultsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.experimentResults = action.payload.data || action.payload;
      })
      .addCase(fetchExperimentResultsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearSuccessMessage } =
  appSettingsSlice.actions;

export default appSettingsSlice.reducer;
